import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [property, setProperty] = useState(null);
  const [tenantAssignment, setTenantAssignment] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  const [showTerminateLeaseModal, setShowTerminateLeaseModal] = useState(false);
  
  // Form states
  const [editPropertyForm, setEditPropertyForm] = useState({
    name: '',
    address: '',
    type: '',
    status: ''
  });
  
  const [newExpenseForm, setNewExpenseForm] = useState({
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash'
  });
  
  const [newTicketForm, setNewTicketForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  
  const [terminateLeaseForm, setTerminateLeaseForm] = useState({
    confirmationText: '',
    reason: ''
  });
  
  // Success/error messages
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (currentUser && userData && propertyId) {
      console.log('Setting up property detail page for:', propertyId);
      
      const unsubscribeFunctions = [];

      try {
        // Subscribe to property details
        const propertyUnsubscribe = propertyService.subscribeToPropertyById(
          propertyId,
          (propertyData) => {
            console.log('Property details received:', propertyData);
            setProperty(propertyData);
          }
        );
        unsubscribeFunctions.push(propertyUnsubscribe);

        // Subscribe to tenant assignment for this property
        const tenantUnsubscribe = tenantService.subscribeToTenantAssignmentByProperty(
          propertyId,
          (assignment) => {
            console.log('Tenant assignment received:', assignment);
            setTenantAssignment(assignment);
          }
        );
        unsubscribeFunctions.push(tenantUnsubscribe);

        // Subscribe to tickets for this property
        const ticketUnsubscribe = ticketService.subscribeToTicketsByProperty(
          propertyId,
          (tickets) => {
            console.log('Property tickets received:', tickets);
            setTickets(tickets || []);
          }
        );
        unsubscribeFunctions.push(ticketUnsubscribe);

        // Subscribe to expenses for this property
        const expenseUnsubscribe = expenseService.subscribeToExpensesByProperty(
          propertyId,
          (expenses) => {
            console.log('Property expenses received:', expenses);
            setExpenses(expenses || []);
            setLoading(false);
          }
        );
        unsubscribeFunctions.push(expenseUnsubscribe);

        return () => {
          unsubscribeFunctions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
              unsubscribe();
            }
          });
        };
      } catch (error) {
        console.error('Error setting up property detail page subscriptions:', error);
        setProperty(null);
        setTenantAssignment(null);
        setTickets([]);
        setExpenses([]);
        setLoading(false);
      }
    } else {
      console.log('No currentUser, userData, or propertyId:', { currentUser, userData, propertyId });
      setLoading(false);
    }
  }, [currentUser, userData, propertyId]);

  // Handler functions
  const handleEditProperty = async (e) => {
    e.preventDefault();
    try {
      await propertyService.updateProperty(propertyId, editPropertyForm);
      setShowEditPropertyModal(false);
      setMessage({ type: 'success', text: 'Property updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update property: ' + error.message });
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await expenseService.addExpense({
        ...newExpenseForm,
        propertyId,
        amount: parseFloat(newExpenseForm.amount),
        date: new Date(newExpenseForm.date)
      });
      setShowAddExpenseModal(false);
      setNewExpenseForm({
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash'
      });
      setMessage({ type: 'success', text: 'Expense added successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add expense: ' + error.message });
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await ticketService.createTicket({
        ...newTicketForm,
        propertyId,
        propertyName: property?.name,
        status: 'open',
        createdBy: currentUser.uid,
        tenantId: tenantAssignment?.tenantId,
        tenantName: tenantAssignment?.tenantName,
        ownerId: property?.ownerId
      });
      setShowCreateTicketModal(false);
      setNewTicketForm({
        title: '',
        description: '',
        priority: 'medium',
        category: 'general'
      });
      setMessage({ type: 'success', text: 'Ticket created successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create ticket: ' + error.message });
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await ticketService.updateTicket(ticketId, { status: newStatus });
      setMessage({ type: 'success', text: 'Ticket status updated!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update ticket: ' + error.message });
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await ticketService.deleteTicket(ticketId);
        setMessage({ type: 'success', text: 'Ticket deleted successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to delete ticket: ' + error.message });
      }
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await expenseService.deleteExpense(expenseId);
        setMessage({ type: 'success', text: 'Expense deleted successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to delete expense: ' + error.message });
      }
    }
  };

  const handleTerminateLease = async (e) => {
    e.preventDefault();
    if (terminateLeaseForm.confirmationText.toLowerCase() !== 'terminate lease') {
      setMessage({ type: 'error', text: 'Please type "terminate lease" exactly to confirm.' });
      return;
    }
    
    try {
      // Here you would implement the actual lease termination logic
      // For now, we'll just show a success message
      setShowTerminateLeaseModal(false);
      setTerminateLeaseForm({ confirmationText: '', reason: '' });
      setMessage({ type: 'success', text: 'Lease termination request submitted successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to terminate lease: ' + error.message });
    }
  };

  const openEditPropertyModal = () => {
    setEditPropertyForm({
      name: property.name,
      address: property.address,
      type: property.type,
      status: property.status
    });
    setShowEditPropertyModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'vacant':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'maintenance':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex">
          <Sidebar />
          <div className="flex-1 ml-64">
            <div className="p-8">
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Property not found</h3>
                <p className="text-gray-600 mb-4">The property you're looking for doesn't exist or you don't have access to it.</p>
                <Link
                  to="/properties"
                  className="px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-sm"
                >
                  Back to Properties
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const openTickets = tickets.filter(ticket => ticket.status === 'open').length;
  const completedTickets = tickets.filter(ticket => ticket.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1 ml-64">
          {/* Header */}
          <header className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-green-100 sticky top-0 z-10">
            <div className="flex items-center justify-between px-8 py-6">
              <div className="flex items-center space-x-3">
                <Link
                  to="/properties"
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{property.name}</h1>
                  <p className="text-sm text-gray-500">{property.address}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(property.status)}`}>
                  {property.status}
                </span>
                <div className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long' 
                  })}
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="p-8">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success' 
                  ? 'bg-green-100 border border-green-200 text-green-800' 
                  : 'bg-red-100 border border-red-200 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
            
            {/* Property Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Monthly Rent</p>
                    <p className="text-2xl font-bold text-gray-800">
                      ${tenantAssignment?.monthlyRent || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${totalExpenses.toFixed(2)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Open Tickets</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {openTickets}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Completed Tickets</p>
                    <p className="text-2xl font-bold text-green-600">
                      {completedTickets}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-green-100">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'overview', name: 'Overview' },
                    { id: 'tenant', name: 'Tenant' },
                    { id: 'maintenance', name: 'Maintenance' },
                    { id: 'expenses', name: 'Expenses' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-800 mb-4">Property Information</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Property Name:</span>
                            <span className="text-sm font-medium text-gray-800">{property.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Address:</span>
                            <span className="text-sm font-medium text-gray-800">{property.address}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Property Type:</span>
                            <span className="text-sm font-medium text-gray-800 capitalize">{property.type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Status:</span>
                            <span className="text-sm font-medium text-gray-800 capitalize">{property.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Created:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {new Date(property.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-medium text-gray-800 mb-4">Quick Actions</h3>
                        <div className="space-y-3">
                          <button 
                            onClick={openEditPropertyModal}
                            className="w-full text-left px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                          >
                            Edit Property Details
                          </button>
                          <button 
                            onClick={() => setShowAddExpenseModal(true)}
                            className="w-full text-left px-4 py-3 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-green-200 hover:border-green-300"
                          >
                            Add New Expense
                          </button>
                          <button 
                            onClick={() => setActiveTab('maintenance')}
                            className="w-full text-left px-4 py-3 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-purple-200 hover:border-purple-300"
                          >
                            View Maintenance History
                          </button>
                          <button 
                            onClick={() => navigate('/tickets')}
                            className="w-full text-left px-4 py-3 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-orange-200 hover:border-orange-300"
                          >
                            View All Tickets
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tenant Tab */}
                {activeTab === 'tenant' && (
                  <div className="space-y-6">
                    {tenantAssignment ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-lg font-medium text-gray-800 mb-4">Current Tenant</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Name:</span>
                              <span className="text-sm font-medium text-gray-800">{tenantAssignment.tenantName}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Email:</span>
                              <span className="text-sm font-medium text-gray-800">{tenantAssignment.tenantEmail}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Phone:</span>
                              <span className="text-sm font-medium text-gray-800">{tenantAssignment.phone}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Monthly Rent:</span>
                              <span className="text-sm font-medium text-gray-800">${tenantAssignment.monthlyRent}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Lease Start:</span>
                              <span className="text-sm font-medium text-gray-800">
                                {new Date(tenantAssignment.leaseStartDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Lease End:</span>
                              <span className="text-sm font-medium text-gray-800">
                                {new Date(tenantAssignment.leaseEndDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Status:</span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(tenantAssignment.status)}`}>
                                {tenantAssignment.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-lg font-medium text-gray-800 mb-4">Tenant Actions</h3>
                          <div className="space-y-3">
                            <button 
                              onClick={() => window.open(`mailto:${tenantAssignment.tenantEmail}`, '_blank')}
                              className="w-full text-left px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                            >
                              Contact Tenant
                            </button>
                            <button 
                              onClick={() => alert('Lease renewal feature coming soon!')}
                              className="w-full text-left px-4 py-3 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-green-200 hover:border-green-300"
                            >
                              Renew Lease
                            </button>
                            <button 
                              onClick={() => alert('Lease modification feature coming soon!')}
                              className="w-full text-left px-4 py-3 text-sm text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors border border-yellow-200 hover:border-yellow-300"
                            >
                              Modify Lease Terms
                            </button>
                                                         <button 
                               onClick={() => setShowTerminateLeaseModal(true)}
                               className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200 hover:border-red-300"
                             >
                               Terminate Lease
                             </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">No tenant assigned</h3>
                        <p className="text-gray-600 mb-4">This property is currently vacant.</p>
                        <button 
                          onClick={() => setShowCreateTenantModal(true)}
                          className="px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-sm"
                        >
                          Create Tenant Account
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Maintenance Tab */}
                {activeTab === 'maintenance' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-800">Maintenance Tickets</h3>
                      <button 
                        onClick={() => setShowCreateTicketModal(true)}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Create New Ticket
                      </button>
                    </div>
                    
                    {tickets.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">No maintenance tickets</h3>
                        <p className="text-gray-600">No maintenance requests have been submitted for this property.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {tickets.map((ticket) => (
                          <div key={ticket.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-800">{ticket.title}</h4>
                                <p className="text-xs text-gray-500">{ticket.description}</p>
                                <p className="text-xs text-gray-400">
                                  {new Date(ticket.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTicketStatusColor(ticket.status)}`}>
                                {ticket.status}
                              </span>
                              <div className="flex space-x-1">
                                {ticket.status === 'open' && (
                                  <button
                                    onClick={() => handleUpdateTicketStatus(ticket.id, 'in_progress')}
                                    className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                  >
                                    Start
                                  </button>
                                )}
                                {ticket.status === 'in_progress' && (
                                  <button
                                    onClick={() => handleUpdateTicketStatus(ticket.id, 'completed')}
                                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                  >
                                    Complete
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteTicket(ticket.id)}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Expenses Tab */}
                {activeTab === 'expenses' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-800">Property Expenses</h3>
                      <button 
                        onClick={() => setShowAddExpenseModal(true)}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Add New Expense
                      </button>
                    </div>
                    
                    {expenses.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">No expenses recorded</h3>
                        <p className="text-gray-600">No expenses have been recorded for this property yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {expenses.map((expense) => (
                          <div key={expense.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-800">{expense.description}</h4>
                                <p className="text-xs text-gray-500">{expense.category}</p>
                                <p className="text-xs text-gray-400">
                                  {new Date(expense.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-800">${expense.amount}</p>
                              <p className="text-xs text-gray-500">{expense.paymentMethod}</p>
                              <button
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="mt-1 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Property Modal */}
      {showEditPropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Edit Property</h3>
            <form onSubmit={handleEditProperty}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Name</label>
                  <input
                    type="text"
                    value={editPropertyForm.name}
                    onChange={(e) => setEditPropertyForm({...editPropertyForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={editPropertyForm.address}
                    onChange={(e) => setEditPropertyForm({...editPropertyForm, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                  <select
                    value={editPropertyForm.type}
                    onChange={(e) => setEditPropertyForm({...editPropertyForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editPropertyForm.status}
                    onChange={(e) => setEditPropertyForm({...editPropertyForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="occupied">Occupied</option>
                    <option value="vacant">Vacant</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Update Property
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditPropertyModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Add New Expense</h3>
            <form onSubmit={handleAddExpense}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={newExpenseForm.description}
                    onChange={(e) => setNewExpenseForm({...newExpenseForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newExpenseForm.amount}
                    onChange={(e) => setNewExpenseForm({...newExpenseForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newExpenseForm.category}
                    onChange={(e) => setNewExpenseForm({...newExpenseForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="utilities">Utilities</option>
                    <option value="repairs">Repairs</option>
                    <option value="insurance">Insurance</option>
                    <option value="taxes">Taxes</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={newExpenseForm.date}
                    onChange={(e) => setNewExpenseForm({...newExpenseForm, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={newExpenseForm.paymentMethod}
                    onChange={(e) => setNewExpenseForm({...newExpenseForm, paymentMethod: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Expense
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddExpenseModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Create New Ticket</h3>
            <form onSubmit={handleCreateTicket}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newTicketForm.title}
                    onChange={(e) => setNewTicketForm({...newTicketForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTicketForm.description}
                    onChange={(e) => setNewTicketForm({...newTicketForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows="3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newTicketForm.priority}
                    onChange={(e) => setNewTicketForm({...newTicketForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newTicketForm.category}
                    onChange={(e) => setNewTicketForm({...newTicketForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="general">General</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="hvac">HVAC</option>
                    <option value="appliance">Appliance</option>
                    <option value="structural">Structural</option>
                  </select>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Ticket
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateTicketModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

             {/* Create Tenant Modal */}
       {showCreateTenantModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-2xl p-6 w-full max-w-md">
             <h3 className="text-lg font-medium text-gray-800 mb-4">Create Tenant Account</h3>
             <p className="text-gray-600 mb-4">This feature will be implemented soon. For now, you can create tenant accounts from the Tenants page.</p>
             <div className="flex space-x-3">
               <button
                 onClick={() => navigate('/tenants')}
                 className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
               >
                 Go to Tenants
               </button>
               <button
                 onClick={() => setShowCreateTenantModal(false)}
                 className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
               >
                 Cancel
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Terminate Lease Modal */}
       {showTerminateLeaseModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
             <div className="flex items-center space-x-3 mb-4">
               <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                 <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                 </svg>
               </div>
               <h3 className="text-lg font-medium text-gray-800">Terminate Lease</h3>
             </div>
             
             <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
               <div className="flex items-start space-x-3">
                 <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                 </svg>
                 <div>
                   <h4 className="text-sm font-medium text-red-800 mb-1">Warning: This action cannot be undone</h4>
                   <p className="text-sm text-red-700">
                     Terminating a lease will immediately end the tenant's occupancy and may have legal implications. 
                     Please ensure you have proper documentation and have followed all legal requirements.
                   </p>
                 </div>
               </div>
             </div>

             <form onSubmit={handleTerminateLease}>
               <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Termination</label>
                   <textarea
                     value={terminateLeaseForm.reason}
                     onChange={(e) => setTerminateLeaseForm({...terminateLeaseForm, reason: e.target.value})}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                     rows="3"
                     placeholder="Please provide a detailed reason for lease termination..."
                     required
                   />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Type "terminate lease" to confirm
                   </label>
                   <input
                     type="text"
                     value={terminateLeaseForm.confirmationText}
                     onChange={(e) => setTerminateLeaseForm({...terminateLeaseForm, confirmationText: e.target.value})}
                     className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                       terminateLeaseForm.confirmationText && 
                       terminateLeaseForm.confirmationText.toLowerCase() !== 'terminate lease'
                         ? 'border-red-300 focus:ring-red-500 bg-red-50'
                         : 'border-gray-300 focus:ring-red-500'
                     }`}
                     placeholder="terminate lease"
                     required
                   />
                   {terminateLeaseForm.confirmationText && 
                    terminateLeaseForm.confirmationText.toLowerCase() !== 'terminate lease' && (
                     <p className="text-sm text-red-600 mt-1">
                       Please type "terminate lease" exactly to confirm this action.
                     </p>
                   )}
                 </div>
               </div>
               
               <div className="flex space-x-3 mt-6">
                 <button
                   type="submit"
                   disabled={terminateLeaseForm.confirmationText.toLowerCase() !== 'terminate lease'}
                   className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                 >
                   Terminate Lease
                 </button>
                 <button
                   type="button"
                   onClick={() => {
                     setShowTerminateLeaseModal(false);
                     setTerminateLeaseForm({ confirmationText: '', reason: '' });
                   }}
                   className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
                 >
                   Cancel
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
    </div>
  );
};

export default PropertyDetailPage; 